<script>
  import { onMount } from "svelte";
  import { fly } from "svelte/transition";
  import { cubicInOut } from "svelte/easing";
  import {
    buildTextFromValues,
    getMonthLength,
    toText,
    getDisplayDate,
  } from "./date-utils";
  import { parse, createFormat } from "./parse.js";
  import DateTimePicker from "./DatePicker.svelte";
  import { writable } from "svelte/store";
  import { createEventDispatcher } from "svelte";
  import { v4 } from "uuid";

  export let textStart = toText($storeStart, formatTokensStart);
  export let textEnd = toText($storeEnd, formatTokensEnd);
  export let startValue = null,
    endValue = null,
    showYearDropdown = true,
    id = UID,
    columnGap = "8px",
    placeholder = "",
    validStart = true,
    validEnd = true,
    disabled = false,
    format = "dd/MM/yyyy HH:mm:ss",
    locale = {},
    min = new Date(defaultDate.getFullYear() - 20, 0, 1),
    max = new Date(defaultDate.getFullYear(), 11, 31, 23, 59, 59, 999),
    visible = false,
    showAbove = false,
    wrapperClass,
    wrapperClassFocus,
    closeOnSelection = false,
    browseWithoutSelecting = false,
    showClearButton,
    connectSymbol = "-",
    inputClass = "pr-2 py-1";

  export let minYear,
    maxYear,
    showScrollYearOptions = false,
    yearOptionSize;

  export let fromTitleText = "From: ",
    fromTitleStyle = "font-bold",
    toTitleText = "To: ",
    toTitleStyle = "font-bold";

    onMount(() => {
      if (/iP(hone|od|ad)/.test(navigator.userAgent) || /Safari/.test(navigator.userAgent)) {
        isIos = true;
      }
    })

  const dispatch = createEventDispatcher();
  const UID = v4();
  const defaultDate = /* @__PURE__ */ new Date();
  const innerStoreStart = writable(null);
  const innerStoreEnd = writable(null);

  $: startMax = $storeEnd ?? max;
  $: endMin = $storeStart ?? min;

  const valuesStart = {
    yyyy: "",
    MM: "",
    dd: "",
    HH: "",
    mm: "",
    ss: "",
  };

  const valuesEnd = {
    yyyy: "",
    MM: "",
    dd: "",
    HH: "",
    mm: "",
    ss: "",
  };

  const storeStart = (() => {
    return {
      subscribe: innerStoreStart.subscribe,
      set: (date) => {
        if (date === null || date === void 0) {
          innerStoreStart.set(null);
          if (startValue) {
            startValue = null;
          }
        } else if (date.getTime() !== $innerStoreStart?.getTime()) {
          innerStoreStart.set(date);
          if (startValue?.getTime() !== date.getTime()) {
            startValue = date;
          }
        }
      },
    };
  })();

  const storeEnd = (() => {
    return {
      subscribe: innerStoreEnd.subscribe,
      set: (date) => {
        if (date === null || date === void 0) {
          innerStoreEnd.set(null);
          if (endValue) {
            endValue = null;
          }
        } else if (date.getTime() !== $innerStoreEnd?.getTime()) {
          innerStoreEnd.set(date);
          if (endValue?.getTime() !== date.getTime()) {
            endValue = date;
          }
        }
      },
    };
  })();

  let previousKeyDown,
      isIos = false;
  let backUpStart, backUpend, backUpSaved;

  const hideSelector = (refresh) => {
    if (!visible) return;
    visible = false;
    if (refresh) {
      dispatch("change");
    } else {
      storeStart.set(backUpStart);
      storeEnd.set(backUpend);
    }
    backUpStart = null;
    backUpend = null;
    backUpSaved = false;
  };

  $: storeStart.set(startValue);
  $: storeEnd.set(endValue);

  $: formatTokensStart = createFormat(format);
  $: formatTokensEnd = createFormat(format);
  $: valueToTextStart($storeStart, formatTokensStart);
  $: valueToTextEnd($storeEnd, formatTokensEnd);
  function valueToTextStart(value2, formatTokens2) {
    textStart = toText(value2, formatTokens2);
  }

  function valueToTextEnd(value2, formatTokens2) {
    textEnd = toText(value2, formatTokens2);
  }

  $: textToValueStart(textStart, formatTokensStart);
  $: textToValueEnd(textEnd, formatTokensEnd);
  function textToValueStart(text2, formatTokens2) {
    if (text2.length) {
      const result = parse(text2, formatTokens2, $storeStart);
      if (result.valid) {
        validStart = true;
        storeStart.set(result.date);
        valuesStart.yyyy = result.year;
        valuesStart.MM = result.month?.toString().padStart(2, "0");
        valuesStart.dd = result.day?.toString().padStart(2, "0");
        valuesStart.HH = result.hours?.toString().padStart(2, "0");
        valuesStart.mm = result.minutes?.toString().padStart(2, "0");
        valuesStart.ss = result.seconds?.toString().padStart(2, "0");
      } else {
        validStart = false;
      }
    } else {
      validStart = true;
      if (startValue) {
        startValue = null;
        storeStart.set(null);
      }
    }
  }
  function textToValueEnd(text2, formatTokens2) {
    if (text2.length) {
      const result = parse(text2, formatTokens2, $storeEnd);
      if (result.valid) {
        validEnd = true;
        storeEnd.set(result.date);
        valuesEnd.yyyy = result.year;
        valuesEnd.MM = result.month?.toString().padStart(2, "0");
        valuesEnd.dd = result.day?.toString().padStart(2, "0");
        valuesEnd.HH = result.hours?.toString().padStart(2, "0");
        valuesEnd.mm = result.minutes?.toString().padStart(2, "0");
        valuesEnd.ss = result.seconds?.toString().padStart(2, "0");
      } else {
        validEnd = false;
      }
    } else {
      validEnd = true;
      if (endValue) {
        endValue = null;
        storeEnd.set(null);
      }
    }
  }

  function onFocusOut(e) {
    if (
      e?.currentTarget instanceof HTMLElement &&
      e.relatedTarget &&
      e.relatedTarget instanceof Node &&
      e.currentTarget.contains(e.relatedTarget)
    ) {
      return;
    } else {
      if(!isIos)
        hideSelector();
    }
  }

  function keydown(e) {
    if (e.key === "Escape" && visible) {
      hideSelector();
      e.preventDefault();
      e.stopPropagation();
    } else if (e.key === "Enter") {
      if (visible === false) visible = true;
      else hideSelector();
      e.preventDefault();
    }
  }

  function onSelect(e) {}

  export let dynamicPositioning = false;
  let InputElement;
  let pickerElement;
  let pickerLeftPosition = null;
  function setDatePickerPosition() {
    pickerLeftPosition = null;
    if (visible && pickerElement && dynamicPositioning) {
      const inputRect = InputElement.getBoundingClientRect();
      const horizontalOverflow = pickerElement.offsetWidth - inputRect.width;
      const bottomThreshold = inputRect.bottom + pickerElement.offsetHeight + 5;
      const rightThreshold = inputRect.left + pickerElement.offsetWidth + 5;
      if (bottomThreshold > window.innerHeight) {
        showAbove = true;
      }
      if (rightThreshold > window.innerWidth) {
        pickerLeftPosition = -horizontalOverflow;
        if (inputRect.left < horizontalOverflow + 5) {
          const windowCenterPos = window.innerWidth / 2;
          const newPos = windowCenterPos - pickerElement.offsetWidth / 2;
          pickerLeftPosition = newPos - inputRect.left;
        }
      }
    }
  }
  function flyAutoPosition(node) {
    setDatePickerPosition();
    return fly(node, {
      duration: 200,
      easing: cubicInOut,
      y: showAbove ? 5 : -5,
    });
  }

  const moveFocus = (current) => {
    const focussable = Array.prototype.filter.call(
      document.querySelectorAll("input:not([disabled])"),
      function (element) {
        //check for visibility while always include the current activeElement
        return (
          element.offsetWidth > 0 ||
          element.offsetHeight > 0 ||
          element === document.activeElement
        );
      },
    );

    if (focussable.indexOf(current) > -1) {
      if (focussable.indexOf(current) === focussable.length - 1) current.blur();
      else focussable[focussable.indexOf(current) + 1]?.focus();
    }
  };

  const onInputKeyDownStart = (e, keyID) => {
    const { key } = e;
    // filter invalid key press
    if (key.length === 1 && !key.match(/^[0-9]+$/) && !e.ctrlKey)
      e.preventDefault();
    // handle up/down key press
    else if (key === "ArrowUp" || key === "ArrowDown") {
      let inputValue = e.target.value;
      switch (keyID) {
        case "yyyy":
          if (inputValue && parseInt(inputValue)) {
            if (
              Number(inputValue) < startMax.getFullYear() &&
              key === "ArrowUp"
            )
              inputValue = (parseInt(inputValue) + 1).toString();
            if (Number(inputValue) > min.getFullYear() && key === "ArrowDown")
              inputValue = (parseInt(inputValue) - 1).toString();
          } else {
            inputValue = "1900";
          }
          break;
        case "MM":
          if (inputValue && parseInt(inputValue)) {
            if (Number(inputValue) < 12 && key === "ArrowUp")
              inputValue = (parseInt(inputValue) + 1).toString();
            if (Number(inputValue) > 1 && key === "ArrowDown")
              inputValue = (parseInt(inputValue) - 1).toString();
          } else {
            inputValue = "01";
          }
          if (inputValue && inputValue.length === 1) {
            inputValue = inputValue.padStart(2, "0");
          }
          break;
        case "dd":
          if (inputValue && parseInt(inputValue)) {
            const maxMonthLength = getMonthLength(
              valuesStart.yyyy ? valuesStart.yyyy : 1,
              valuesStart.MM ? valuesStart.MM - 1 : 0,
            );
            if (Number(inputValue) < maxMonthLength && key === "ArrowUp")
              inputValue = (parseInt(inputValue) + 1).toString();
            if (Number(inputValue) > 1 && key === "ArrowDown")
              inputValue = (parseInt(inputValue) - 1).toString();
          } else {
            inputValue = "01";
          }
          if (inputValue && inputValue.length === 1)
            inputValue = inputValue.padStart(2, "0");
          break;
        case "HH":
          if (inputValue && parseInt(inputValue)) {
            if (Number(inputValue) < 23 && key === "ArrowUp")
              inputValue = (parseInt(inputValue) + 1).toString();
            if (Number(inputValue) > 0 && key === "ArrowDown")
              inputValue = (parseInt(inputValue) - 1).toString();
          } else {
            inputValue = "01";
          }
          if (inputValue && inputValue.length === 1) {
            inputValue = inputValue.padStart(2, "0");
          }
          break;
        case "mm":
          if (inputValue && parseInt(inputValue)) {
            if (Number(inputValue) < 59 && key === "ArrowUp")
              inputValue = (parseInt(inputValue) + 1).toString();
            if (Number(inputValue) > 0 && key === "ArrowDown")
              inputValue = (parseInt(inputValue) - 1).toString();
          } else {
            inputValue = "01";
          }
          if (inputValue && inputValue.length === 1) {
            inputValue = inputValue.padStart(2, "0");
          }
          break;
        case "ss":
          if (inputValue && parseInt(inputValue)) {
            if (Number(inputValue) < 59 && key === "ArrowUp")
              inputValue = (parseInt(inputValue) + 1).toString();
            if (Number(inputValue) > 0 && key === "ArrowDown")
              inputValue = (parseInt(inputValue) - 1).toString();
          } else {
            inputValue = "01";
          }
          if (inputValue && inputValue.length === 1) {
            inputValue = inputValue.padStart(2, "0");
          }
          break;
        default:
          break;
      }
      e.target.value = inputValue;
      valuesStart[keyID] = inputValue;
      e.preventDefault();
    } else if (
      // handle overwrite press
      key.match(/^[0-9]+$/) &&
      e.target.value.length === e.target.maxLength &&
      e.target.selectionEnd === e.target.selectionStart &&
      e.target.maxLength > e.target.selectionStart
    ) {
      e.preventDefault();
      const { selectionStart } = e.target;
      e.target.value =
        e.target.value.substring(0, selectionStart) +
        key +
        e.target.value.substring(selectionStart + 1);
      e.target.selectionEnd = selectionStart + 1;
      e.target.selectionStart = selectionStart + 1;
      if (selectionStart + 1 === e.target.maxLength) {
        moveFocus(e.target);
      }
    }
  };

  const onInputKeyDownEnd = (e, keyID) => {
    const { key } = e;
    // filter invalid key press
    if (key.length === 1 && !key.match(/^[0-9]+$/) && !e.ctrlKey)
      e.preventDefault();
    // handle up/down key press
    else if (key === "ArrowUp" || key === "ArrowDown") {
      let inputValue = e.target.value;
      switch (keyID) {
        case "yyyy":
          if (inputValue && parseInt(inputValue)) {
            if (Number(inputValue) < max.getFullYear() && key === "ArrowUp")
              inputValue = (parseInt(inputValue) + 1).toString();
            if (
              Number(inputValue) > endMin.getFullYear() &&
              key === "ArrowDown"
            )
              inputValue = (parseInt(inputValue) - 1).toString();
          } else {
            inputValue = "1900";
          }
          break;
        case "MM":
          if (inputValue && parseInt(inputValue)) {
            if (Number(inputValue) < 12 && key === "ArrowUp")
              inputValue = (parseInt(inputValue) + 1).toString();
            if (Number(inputValue) > 1 && key === "ArrowDown")
              inputValue = (parseInt(inputValue) - 1).toString();
          } else {
            inputValue = "01";
          }
          if (inputValue && inputValue.length === 1) {
            inputValue = inputValue.padStart(2, "0");
          }
          break;
        case "dd":
          if (inputValue && parseInt(inputValue)) {
            const maxMonthLength = getMonthLength(
              valuesEnd.yyyy ? valuesEnd.yyyy : 1,
              valuesEnd.MM ? valuesEnd.MM - 1 : 0,
            );
            if (Number(inputValue) < maxMonthLength && key === "ArrowUp")
              inputValue = (parseInt(inputValue) + 1).toString();
            if (Number(inputValue) > 1 && key === "ArrowDown")
              inputValue = (parseInt(inputValue) - 1).toString();
          } else {
            inputValue = "01";
          }
          if (inputValue && inputValue.length === 1)
            inputValue = inputValue.padStart(2, "0");
          break;
        case "HH":
          if (inputValue && parseInt(inputValue)) {
            if (Number(inputValue) < 23 && key === "ArrowUp")
              inputValue = (parseInt(inputValue) + 1).toString();
            if (Number(inputValue) > 0 && key === "ArrowDown")
              inputValue = (parseInt(inputValue) - 1).toString();
          } else {
            inputValue = "01";
          }
          if (inputValue && inputValue.length === 1) {
            inputValue = inputValue.padStart(2, "0");
          }
          break;
        case "mm":
          if (inputValue && parseInt(inputValue)) {
            if (Number(inputValue) < 59 && key === "ArrowUp")
              inputValue = (parseInt(inputValue) + 1).toString();
            if (Number(inputValue) > 0 && key === "ArrowDown")
              inputValue = (parseInt(inputValue) - 1).toString();
          } else {
            inputValue = "01";
          }
          if (inputValue && inputValue.length === 1) {
            inputValue = inputValue.padStart(2, "0");
          }
          break;
        case "ss":
          if (inputValue && parseInt(inputValue)) {
            if (Number(inputValue) < 59 && key === "ArrowUp")
              inputValue = (parseInt(inputValue) + 1).toString();
            if (Number(inputValue) > 0 && key === "ArrowDown")
              inputValue = (parseInt(inputValue) - 1).toString();
          } else {
            inputValue = "01";
          }
          if (inputValue && inputValue.length === 1) {
            inputValue = inputValue.padStart(2, "0");
          }
          break;
        default:
          break;
      }
      e.target.value = inputValue;
      valuesEnd[keyID] = inputValue;
      e.preventDefault();
    } else if (
      // handle overwrite press
      key.match(/^[0-9]+$/) &&
      e.target.value.length === e.target.maxLength &&
      e.target.selectionEnd === e.target.selectionStart &&
      e.target.maxLength > e.target.selectionStart
    ) {
      e.preventDefault();
      const { selectionStart } = e.target;
      e.target.value =
        e.target.value.substring(0, selectionStart) +
        key +
        e.target.value.substring(selectionStart + 1);
      e.target.selectionEnd = selectionStart + 1;
      e.target.selectionStart = selectionStart + 1;
      if (selectionStart + 1 === e.target.maxLength) {
        moveFocus(e.target);
      }
    }
  };

  const moveToNextInput = (e) => {
    if (
      e.target.value.length === e.target.maxLength &&
      e.target.selectionEnd === e.target.maxLength
    ) {
      moveFocus(e.target);
    }
  };

  const updateValueStart = (e, key) => {
    let inputValue = e.target.value;

    switch (key) {
      case "yyyy":
        if (inputValue && parseInt(inputValue)) {
          if (Number(inputValue) > max.getFullYear())
            inputValue = max.getFullYear();
          else if (Number(inputValue) < min.getFullYear())
            inputValue = min.getFullYear();
        } else inputValue = min.getFullYear();
        break;
      case "MM":
        if (inputValue && parseInt(inputValue)) {
          if (Number(inputValue) > 12) inputValue = 12;
          else if (Number(inputValue) < 1) inputValue = 1;
        }
        if (inputValue && inputValue.length === 1)
          inputValue = inputValue.padStart(2, "0");
        break;
      case "dd":
        if (inputValue && parseInt(inputValue)) {
          const maxMonthLength = getMonthLength(
            valuesStart.yyyy ? valuesStart.yyyy : 1,
            valuesStart.MM ? valuesStart.MM - 1 : 0,
          );
          if (Number(inputValue) > maxMonthLength) inputValue = maxMonthLength;
          else if (Number(inputValue) < 1) inputValue = 1;
        }
        if (inputValue && inputValue.length === 1)
          inputValue = inputValue.padStart(2, "0");
        break;
      case "HH":
        if (inputValue && parseInt(inputValue)) {
          if (Number(inputValue) > 23) inputValue = 23;
          else if (Number(inputValue) < 0) inputValue = 0;
        }
        if (inputValue && inputValue.length === 1)
          inputValue = inputValue.padStart(2, "0");
        break;
      case "mm":
        if (inputValue && parseInt(inputValue)) {
          if (Number(inputValue) > 59) inputValue = 59;
          else if (Number(inputValue) < 0) inputValue = 0;
        }
        if (inputValue && inputValue.length === 1)
          inputValue = inputValue.padStart(2, "0");
        break;
      case "ss":
        if (inputValue && parseInt(inputValue)) {
          if (Number(inputValue) > 59) inputValue = 59;
          else if (Number(inputValue) < 0) inputValue = 0;
        }
        if (inputValue && inputValue.length === 1)
          inputValue = inputValue.padStart(2, "0");
        break;
      default:
        break;
    }

    e.target.value = inputValue;
    valuesStart[key] = inputValue;
    textStart = buildTextFromValues(valuesStart, formatTokensStart);
  };

  const updateValueEnd = (e, key) => {
    let inputValue = e.target.value;

    switch (key) {
      case "yyyy":
        if (inputValue && parseInt(inputValue)) {
          if (Number(inputValue) > max.getFullYear())
            inputValue = max.getFullYear();
          else if (Number(inputValue) < min.getFullYear())
            inputValue = min.getFullYear();
        } else inputValue = min.getFullYear();
        break;
      case "MM":
        if (inputValue && parseInt(inputValue)) {
          if (Number(inputValue) > 12) inputValue = 12;
          else if (Number(inputValue) < 1) inputValue = 1;
        }
        if (inputValue && inputValue.length === 1)
          inputValue = inputValue.padStart(2, "0");
        break;
      case "dd":
        if (inputValue && parseInt(inputValue)) {
          const maxMonthLength = getMonthLength(
            valuesEnd.yyyy ? valuesEnd.yyyy : 1,
            valuesEnd.MM ? valuesEnd.MM - 1 : 0,
          );
          if (Number(inputValue) > maxMonthLength) inputValue = maxMonthLength;
          else if (Number(inputValue) < 1) inputValue = 1;
        }
        if (inputValue && inputValue.length === 1)
          inputValue = inputValue.padStart(2, "0");
        break;
      case "HH":
        if (inputValue && parseInt(inputValue)) {
          if (Number(inputValue) > 23) inputValue = 23;
          else if (Number(inputValue) < 0) inputValue = 0;
        }
        if (inputValue && inputValue.length === 1)
          inputValue = inputValue.padStart(2, "0");
        break;
      case "mm":
        if (inputValue && parseInt(inputValue)) {
          if (Number(inputValue) > 59) inputValue = 59;
          else if (Number(inputValue) < 0) inputValue = 0;
        }
        if (inputValue && inputValue.length === 1)
          inputValue = inputValue.padStart(2, "0");
        break;
      case "ss":
        if (inputValue && parseInt(inputValue)) {
          if (Number(inputValue) > 59) inputValue = 59;
          else if (Number(inputValue) < 0) inputValue = 0;
        }
        if (inputValue && inputValue.length === 1)
          inputValue = inputValue.padStart(2, "0");
        break;
      default:
        break;
    }

    e.target.value = inputValue;
    valuesEnd[key] = inputValue;
    textEnd = buildTextFromValues(valuesEnd, formatTokensEnd);
  };

  const showPicker = () => {
    if (visible || disabled) return;
    visible = true;
    onPlaceholderClick();
  };

  const clearValueStart = () => {
    startValue = null;
    Object.keys(valuesStart).forEach((key) => (valuesStart[key] = ""));
    dispatch("change");
  };

  const clearValueEnd = () => {
    endValue = null;
    Object.keys(valuesEnd).forEach((key) => (valuesEnd[key] = ""));
    dispatch("change");
  };

  const onPlaceholderClick = () => {
    if (disabled) return;
    // wait for inputs render
    setTimeout(() => {
      if (!UID) return;
      const allInputs = document.getElementsByClassName(UID);
      if (allInputs && allInputs.length) {
        allInputs[0]?.focus();
      }
    }, 100);
  };

  $: if (visible) {
    window.addEventListener(
      "click",
      function eventHandler(e) {
        const dropdownSelect = document.getElementById(id);
        if (
          dropdownSelect &&
          !document.getElementById(id)?.contains(e.target)
        ) {
          hideSelector();
          window.removeEventListener("click", eventHandler, true);
        }
      },
      true,
    );

    window.addEventListener(
      "focus",
      function focusHandler(e) {
        const dropdownSelect = document.getElementById(id);
        if (
          dropdownSelect &&
          !document.getElementById(id)?.contains(e.target)
        ) {
          hideSelector();
          window.removeEventListener("focus", focusHandler, true);
        }
      },
      true,
    );

    if (document.onkeydown) previousKeyDown = document.onkeydown;

    document.onkeydown = (event) => {
      if (event.key === "Escape") {
        hideSelector();
        document.onkeydown = previousKeyDown ?? null;
      }
    };
  }

  export const refreshValue = () => {
    visible = true;
    setTimeout(() => {
      hideSelector();
    }, 50);
  };

  $: if (visible && !backUpSaved) {
    backUpSaved = true;
    backUpStart = startValue;
    backUpend = endValue;
  }
</script>

<div class="flex flex-col">
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div
    class="date-time-field {disabled
      ? 'bg-gray-200'
      : ''} {wrapperClass} {visible ? wrapperClassFocus : ''}"
    on:focusout={onFocusOut}
    on:keydown={keydown}
    {id}
    bind:this={InputElement}
  >
    <div
      class="flex flex-row flex-1 relative {inputClass} {disabled
        ? 'cursor-not-allowed'
        : ''}"
      on:click={showPicker}
      on:focus={showPicker}
      style="column-gap: {columnGap};"
    >
      <span
        class="relative flex flex-row gap-x-0 flex-1 items-center pl-3"
        on:click={showPicker}
        on:focus={showPicker}
        tabindex="0"
        class:invalid={!validStart && !showClearButton}
      >
        {#if placeholder && !visible && !startValue}
          <span
            class="opacity-60 w-full {disabled
              ? 'cursor-not-allowed'
              : 'cursor-pointer'}"
            on:click={onPlaceholderClick}
          >
            {placeholder}
          </span>
        {:else}
          {#each formatTokensStart as token, index}
            {#if typeof token == "string"}
              {#if showYearDropdown || index < 3}
                <span>{token}</span>
              {/if}
            {:else if showYearDropdown || index < 3}
              <input
                value={startValue ? token.toString(startValue) : ""}
                type="text"
                class="{UID} text-primary {disabled
                  ? 'cursor-not-allowed'
                  : ''} {token.id.length === 2 ? 'w-[23px]' : 'w-[43px]'}"
                maxlength={token.id.length}
                on:blur={(e) => updateValueStart(e, token.id)}
                on:keydown={(e) => onInputKeyDownStart(e, token.id)}
                on:input={moveToNextInput}
                on:focus={showPicker}
                {disabled}
              />
            {/if}
          {/each}
          {#if showClearButton && $storeStart}
            <button
              class="ml-1 right-0"
              on:click|stopPropagation={clearValueStart}
              {disabled}
            >
              <i
                class="fa fa-times"
                style="color: #6F726C;"
                aria-hidden="true"
              />
            </button>
          {/if}
        {/if}
      </span>

      <div class="flex-1 flex justify-center">
        {connectSymbol ?? ""}
      </div>

      <span
        class="relative flex flex-row gap-x-0 flex-1 items-center pr-3"
        on:click={showPicker}
        on:focus={showPicker}
        tabindex="0"
        class:invalid={!validEnd && !showClearButton}
      >
        {#if placeholder && !visible && !endValue}
          <span
            class="opacity-60 w-full {disabled
              ? 'cursor-not-allowed'
              : 'cursor-pointer'}"
            on:click={onPlaceholderClick}
          >
            {placeholder}
          </span>
        {:else}
          {#each formatTokensEnd as token, index}
            {#if typeof token == "string"}
              {#if showYearDropdown || index < 3}
                <span>{token}</span>
              {/if}
            {:else if showYearDropdown || index < 3}
              <input
                value={endValue ? token.toString(endValue) : ""}
                type="text"
                class="{UID} text-primary {disabled
                  ? 'cursor-not-allowed'
                  : ''} {token.id.length === 2 ? 'w-[23px]' : 'w-[43px]'}"
                maxlength={token.id.length}
                on:blur={(e) => updateValueEnd(e, token.id)}
                on:keydown={(e) => onInputKeyDownEnd(e, token.id)}
                on:input={moveToNextInput}
                on:focus={showPicker}
                {disabled}
              />
            {/if}
          {/each}
          {#if showClearButton && $storeEnd}
            <button
              class="ml-1 right-0"
              on:click|stopPropagation={clearValueEnd}
              {disabled}
            >
              <i
                class="fa fa-times"
                style="color: #6F726C;"
                aria-hidden="true"
              />
            </button>
          {/if}
        {/if}
      </span>
    </div>

    {#if visible && !disabled}
      <div
        class="picker border border-1 border-secondary shadow shadow-xl shadow-[#8A3ED7]"
        class:visible
        class:above={showAbove}
        transition:flyAutoPosition
        bind:this={pickerElement}
        style:--picker-left-position="{pickerLeftPosition}px"
      >
        <div class="flex flex-row">
          <DateTimePicker
            on:select={onSelect}
            bind:value={$storeStart}
            {min}
            max={startMax}
            {locale}
            {browseWithoutSelecting}
            {showYearDropdown}
            titleText={fromTitleText}
            titleStyle={fromTitleStyle}
            {minYear}
            {maxYear}
            {showScrollYearOptions}
            {yearOptionSize}
          />
          <DateTimePicker
            on:select={onSelect}
            bind:value={$storeEnd}
            min={endMin}
            {max}
            {locale}
            {browseWithoutSelecting}
            {showYearDropdown}
            titleText={toTitleText}
            titleStyle={toTitleStyle}
            {minYear}
            {maxYear}
            {showScrollYearOptions}
            {yearOptionSize}
          />
        </div>
        <div
          class="flex flex-row-reverse w-full justify-between gap-x-2 bg-white p-3 picker-buttons"
        >
          <div class="flex flex-row gap-x-2">
            <button
              class="btn btn-sm btn-accent"
              on:click|stopPropagation={() => hideSelector()}
            >
              Cancel
            </button>
            <button
              class="btn btn-sm btn-secondary"
              on:click|stopPropagation={() => hideSelector(true)}
            >
              Apply
            </button>
          </div>
          {#if $storeStart && $storeEnd}
            <div class="flex flex-row gap-x-2">
              {getDisplayDate($storeStart ?? "")}
              {connectSymbol}
              {getDisplayDate($storeEnd ?? "")}
            </div>
          {/if}
        </div>
      </div>
    {/if}
  </div>
</div>

<style>
  .date-time-field {
    position: relative;
    display: flex;
    flex-direction: row;
    gap: 0;
  }

  input[type="text"] {
    margin: 0;
    padding: 0 2px;
    height: 18px;
    line-height: 18px;
    background: transparent;
  }

  input[type="text"]:focus {
    outline: 0;
  }

  .invalid {
    border: 1px solid rgba(249, 47, 114, 0.5);
    background-color: rgba(249, 47, 114, 0.1);
    border-radius: 5px;
  }

  .invalid:focus {
    border-color: #f92f72;
    box-shadow: 0px 0px 0px 2px rgba(249, 47, 114, 0.5);
  }

  .picker {
    display: none;
    position: absolute;
    padding: 1px;
    left: var(--picker-left-position);
    z-index: 100;
    top: 100%;
    flex-direction: column;
  }
  .picker.above {
    bottom: 100%;
    top: auto !important;
  }
  .picker.visible {
    display: flex;
  }

  .picker-buttons {
    box-shadow:
      0px 2px 6px rgba(0, 0, 0, 0.08),
      0px 2px 6px rgba(0, 0, 0, 0.11);
  }
</style>
